import { Logo } from '@/components/logo.component';
import Button from '@components/base/button.component.tsx';
import CardActions from '@components/base/card/card-actions.component.tsx';
import CardHeader from '@components/base/card/card-header.component.tsx';
import Card from '@components/base/card/card.component.tsx';
import { CircularLoaderContainer } from '@components/base/circular-loader-container.component.tsx';
import Container from '@components/base/container.component.tsx';
import { useSignOut } from '@hooks/use-sign-out.ts';
import {
  Lock as LockIcon,
  Logout as LogoutIcon,
  PeopleAlt as PeopleAltIcon,
} from '@mui/icons-material';
import { List, Pagination, TextField, ThemeProvider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LinkButton } from '../components/auth-link-button.component';
import { ChangeEvent, useState } from 'react';
import Box from '@/components/base/box.component';
import { theme } from '@/config/theme';
import { useGetAllContactEmployees } from '@/api/business-partner/business-partner.hooks.ts';
import { useMe } from '@/api/auth/auth.hooks.ts';

export function AuthImpersonatePage() {
  const navigate = useNavigate();
  const signOut = useSignOut();
  const [search, setSearch] = useState('');
  const { data: contactEmployeesData, isPending: isContactEmployeesPending } =
    useGetAllContactEmployees();
  const contactEmployees = contactEmployeesData || [];
  const [page, setPage] = useState(0);
  const contactEmployeesPerPage = 5;
  const filteredContactEmployees = contactEmployees?.filter(
    (employee) =>
      employee.name.toLowerCase().includes(search.toLowerCase()) ||
      employee.email?.toLowerCase().includes(search.toLowerCase()) ||
      employee.businessPartner?.name
        .toLowerCase()
        .includes(search.toLowerCase()),
  );
  const contactEmployeesPageCount = Math.ceil(
    filteredContactEmployees?.length / contactEmployeesPerPage,
  );
  const contactEmployeesPage = filteredContactEmployees?.slice(
    page * contactEmployeesPerPage,
    (page + 1) * contactEmployeesPerPage,
  );
  const { data: me, isPending } = useMe();
  const isInternal = me?.accountType === 'INTERNAL';

  const handleSignOut = () => {
    signOut();
    navigate('/auth');
  };

  const handleInternalClick = () => {
    navigate('/i');
  };

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="sm">
        <Logo variant="dark" />
        <Card>
          <CardHeader
            icon={<PeopleAltIcon />}
            title="Als Benutzer ausgeben"
            subheader={
              'Wählen Sie den Benutzer aus, dessen Daten Sie sehen möchten.'
            }
          />
          <Container
            sx={{
              p: '0 !important',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ width: '100%', px: 2, py: '1 !important' }}>
              <TextField
                label="Suche"
                variant={'standard'}
                type="search"
                sx={{ width: '100%' }}
                onChange={(event) => setSearch(event.target.value)}
                value={search}
              />
            </Box>
            <List sx={{ width: '100%' }}>
              {contactEmployeesPage.map((employee) => {
                return <LinkButton key={employee.id} employee={employee} />;
              })}
            </List>
            <Pagination
              sx={{
                mx: 'auto',
                py: 2,
              }}
              count={contactEmployeesPageCount}
              page={page}
              onChange={handlePageChange}
            />
          </Container>
          {isPending ? <CircularLoaderContainer /> : undefined}
          <CardActions>
            {isInternal ? (
              <>
                <Button
                  startIcon={<LockIcon />}
                  size={'small'}
                  onClick={handleInternalClick}
                >
                  Interner Bereich
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  startIcon={<PeopleAltIcon />}
                  size={'small'}
                >
                  Zurück zur Accountauswahl
                </Button>
              </>
            ) : undefined}
            <Button
              onClick={handleSignOut}
              startIcon={<LogoutIcon />}
              size={'small'}
            >
              Abmelden
            </Button>
          </CardActions>
        </Card>
      </Container>
    </ThemeProvider>
  );
}
